import React from "react";
import type { Brand } from "../lib/ChatbotConfig";
import { brandForHostname } from "../utils/validateChatbotConfig";
import debugLog from "../utils/debugLog";
import {
  HOST_MESSAGES,
  isEmbeddedInIframe,
  isTrustedHost,
  requestParentUrl,
} from "./hostProtocol";

/** How long to wait for the host before giving up on it. */
const HANDSHAKE_TIMEOUT_IN_MS = 500;

export interface ParentBrand {
  brand?: Brand;
  /**
   * False only while the host still has a chance to answer. Callers should hold
   * off rendering until this flips: the brand decides the theme, and reporting
   * a "missing brand" before the handshake finished would be a false alarm.
   */
  settled: boolean;
}

const hostnameOf = (url: string): string =>
  url.includes("://") ? new URL(url).hostname : url.split("/")[0];

/**
 * The brand of the page the iframe is embedded in.
 *
 * The standalone app is served from one brand-neutral domain for all four
 * brands, so it cannot know its brand from its own URL — the embedding page is
 * the only source. Pages running an older embed script never answer, which is
 * what the timeout is for.
 */
const useParentBrand = (): ParentBrand => {
  const [brand, setBrand] = React.useState<Brand | undefined>(undefined);
  const [settled, setSettled] = React.useState<boolean>(
    () => !isEmbeddedInIframe(),
  );

  React.useEffect(() => {
    if (!isEmbeddedInIframe()) return;

    const onHostMessage = (event: MessageEvent): void => {
      if (event.data?.type !== HOST_MESSAGES.setParentUrl) return;

      const url = event.data.url;

      if (typeof url !== "string" || !url) return;

      // The host answering at all settles the question — whether or not its
      // domain identifies a brand.
      setSettled(true);

      let hostname: string;

      try {
        hostname = hostnameOf(url);
      } catch {
        return;
      }

      if (!isTrustedHost(hostname)) {
        debugLog(`Ignoring brand from untrusted host: ${hostname}`);
        return;
      }

      const hostBrand = brandForHostname(hostname);

      if (!hostBrand) return;

      debugLog(`Host page identifies as brand: ${hostBrand}`);
      setBrand(hostBrand);
    };

    const timeout = setTimeout(
      () => setSettled(true),
      HANDSHAKE_TIMEOUT_IN_MS,
    );

    window.addEventListener("message", onHostMessage);
    requestParentUrl();

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("message", onHostMessage);
    };
  }, []);

  return { brand, settled };
};

export default useParentBrand;
